import type { PersistedEstimate } from '@hooks/use-persisted-estimate';
import type {
	ContactFormData,
	PricingEstimationService,
	PricingEstimationTotal,
} from '../../../../types';
import {
	SERVICES,
	buildEstimateDisplay,
	displayEstimateServicePrice,
} from '../../../utils/estimateDisplay';
import type { EstimateServiceBreakdown } from '../../../utils/estimateDisplay';
import {
	resolveEstimateServiceSourceType,
	resolveEstimateSourceType,
} from '../../../utils/estimateSource';
import { setPersistedFormInput } from '../../../utils/persistedFormInput';
import type { WeddingDateData } from '../../../utils/weddingDate';

interface PersistEstimateDataParams {
	estimate: PricingEstimationTotal;
	weddingDate: WeddingDateData;
	guestCount: number;
	rawGuestCount: string;
	selectedServices: PricingEstimationService[];
	unavailableServices: PricingEstimationService[];
	storefrontId: string;
	contactData?: ContactFormData;
	setEstimateDisplay: (
		display: ReturnType<typeof buildEstimateDisplay>,
	) => void;
	setEstimateServiceBreakdown: (breakdown: EstimateServiceBreakdown) => void;
	persistEstimate: (estimate: PersistedEstimate) => void;
}

export const persistEstimateData = ({
	estimate,
	weddingDate,
	guestCount,
	rawGuestCount,
	selectedServices,
	unavailableServices,
	storefrontId,
	contactData,
	setEstimateDisplay,
	setEstimateServiceBreakdown,
	persistEstimate,
}: PersistEstimateDataParams) => {
	const weddingDatePersisted: PersistedEstimate['weddingDate'] =
		weddingDate.dateOption === 'specificDate'
			? { type: 'exact', date: weddingDate.specificDate }
			: {
					type: 'month-year',
					month: weddingDate.month,
					year: Number(weddingDate.year),
				};

	const estimateSourceType = resolveEstimateSourceType(
		estimate,
		selectedServices,
	);

	setEstimateDisplay(
		buildEstimateDisplay({
			selections: Object.fromEntries(selectedServices.map((s) => [s, true])),
			weddingDate: weddingDatePersisted,
			guestCount,
			calculatedTotal: {
				min: estimate.totalCost.minCost,
				max: estimate.totalCost.maxCost,
				isBelowMinimumSpend: estimate.totalCost.isBelowMinimumSpend,
				originalMinCost: estimate.totalCost.originalMinCost,
			},
			estimateSourceType,
		}),
	);

	const serviceBreakdown: EstimateServiceBreakdown = {};
	for (const { key } of SERVICES) {
		const p = estimate[key];
		if (p) {
			serviceBreakdown[key] = {
				price: displayEstimateServicePrice(p),
				sourceType: resolveEstimateServiceSourceType(p.dataSource),
				isFree: p.isFree,
			};
		}
	}
	for (const key of unavailableServices) {
		if (selectedServices.includes(key))
			serviceBreakdown[key] = { unavailable: true };
	}
	setEstimateServiceBreakdown(serviceBreakdown);

	if (storefrontId)
		persistEstimate({
			storefrontId,
			guestCount,
			weddingDate: weddingDatePersisted,
			selections: Object.fromEntries(selectedServices.map((s) => [s, true])),
			estimateSourceType,
			serviceBreakdown,
			calculatedTotal: {
				min: estimate.totalCost.minCost,
				max: estimate.totalCost.maxCost,
				isBelowMinimumSpend: estimate.totalCost.isBelowMinimumSpend,
				originalMinCost: estimate.totalCost.originalMinCost,
			},
			unavailableServices,
			lastUpdated: new Date().toISOString(),
		});

	setPersistedFormInput({
		guestCount: rawGuestCount,
		weddingDate,
		selectedServices,
		contactData,
	});
};
